import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '../types/supabase';

// Configuration validation
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
const authFlow = (import.meta.env.VITE_AUTH_FLOW as 'pkce' | 'implicit') || 'pkce';

// Validation stricte des variables d'environnement et des valeurs placeholder
if (!supabaseUrl || !supabaseAnonKey || 
    supabaseUrl.includes('placeholder') || 
    supabaseAnonKey.includes('placeholder') ||
    supabaseUrl === 'https://placeholder.supabase.co' ||
    supabaseAnonKey === 'placeholder-key') {
  const missing = [];
  if (!supabaseUrl) missing.push('VITE_SUPABASE_URL');
  if (!supabaseAnonKey) missing.push('VITE_SUPABASE_ANON_KEY');
  if (supabaseUrl?.includes('placeholder')) missing.push('VITE_SUPABASE_URL (valeur placeholder détectée)');
  if (supabaseAnonKey?.includes('placeholder')) missing.push('VITE_SUPABASE_ANON_KEY (valeur placeholder détectée)');
  
  const errorMessage = `[SUPABASE CONFIG] Variables manquantes: ${missing.join(', ')}
  
📋 Pour corriger :
1. Cliquez sur "Connect to Supabase" en haut à droite de l'interface
2. Ou créez un fichier .env à la racine du projet avec vos vraies valeurs :
   VITE_SUPABASE_URL=https://your-project.supabase.co
   VITE_SUPABASE_ANON_KEY=your-anon-key
3. Redémarrez le serveur de développement (npm run dev)

🔗 Récupérez vos clés sur : https://supabase.com/dashboard/project/your-project/settings/api`;

  console.error(errorMessage);
  
  if (import.meta.env.DEV) {
    console.warn('[SUPABASE] Configuration manquante - mode dégradé activé');
    // Ne pas faire planter l'app en dev, juste logger l'erreur
  }
}

// Validation du format URL
if (supabaseUrl && !supabaseUrl.includes('placeholder') && !supabaseUrl.startsWith('https://')) {
  throw new Error(`[SUPABASE CONFIG] URL invalide: ${supabaseUrl}. Doit commencer par https://`);
}

// Création du client Supabase avec gestion des erreurs
export const supabase: SupabaseClient<Database> = createClient(
  supabaseUrl || 'https://placeholder.supabase.co', 
  supabaseAnonKey || 'placeholder-key', 
  {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: authFlow,
    debug: import.meta.env.DEV,
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  },
  global: {
    headers: {
      'X-Client-Info': 'cress-guadeloupe-platform'
    }
  }
});

// Helper pour vérifier la configuration
export const isSupabaseConfigured = (): boolean => {
  return !!(supabaseUrl && 
           supabaseAnonKey && 
           supabaseUrl !== 'https://placeholder.supabase.co' && 
           supabaseAnonKey !== 'placeholder-key' &&
           !supabaseUrl.includes('placeholder') &&
           !supabaseAnonKey.includes('placeholder'));
};

// Helpers d'authentification sécurisés
export const auth = {
  /**
   * Connexion avec email/password
   */
  signIn: async (email: string, password: string) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase n\'est pas configuré. Veuillez cliquer sur "Connect to Supabase" en haut à droite pour configurer la connexion.');
    }
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });
      
      if (error) {
        console.error('[AUTH] Erreur connexion:', error.message);
        
        // Créer une erreur personnalisée avec code et message user-friendly
        const customError = new Error();
        
        if (error.message.includes('Invalid login credentials')) {
          customError.name = 'AuthError';
          (customError as any).code = 'invalid_credentials';
          customError.message = 'Email ou mot de passe incorrect. Veuillez vérifier vos identifiants et réessayer.';
        } else if (error.message.includes('Email not confirmed')) {
          customError.name = 'AuthError';
          (customError as any).code = 'email_not_confirmed';
          customError.message = 'Veuillez confirmer votre email avant de vous connecter.';
        } else if (error.message.includes('Too many requests')) {
          customError.name = 'AuthError';
          (customError as any).code = 'too_many_requests';
          customError.message = 'Trop de tentatives. Veuillez patienter quelques minutes.';
        } else {
          customError.name = 'AuthError';
          (customError as any).code = 'unknown_error';
          customError.message = error.message;
        }
        
        throw customError;
      }
      
      console.log('[AUTH] Connexion réussie:', data.user?.email);
      return { data, error: null };
    } catch (error) {
      // Si c'est déjà notre erreur personnalisée, la relancer telle quelle
      if (error instanceof Error && error.name === 'AuthError') {
        throw error;
      }
      
      console.error('[AUTH] Erreur inattendue:', error);
      throw error;
    }
  },

  /**
   * Inscription avec email/password
   */
  signUp: async (email: string, password: string, metadata: any = {}) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase n\'est pas configuré. Veuillez cliquer sur "Connect to Supabase" en haut à droite pour configurer la connexion.');
    }
    
    try {
      const { data, error } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          data: metadata,
          emailRedirectTo: `${window.location.origin}/auth/callback`
        },
      });
      
      if (error) {
        console.error('[AUTH] Erreur inscription:', error.message);

        if (error.message.includes('User already registered')) {
          throw new Error('Cet email est déjà enregistré. Vous pouvez vous connecter.');
        }
        if (error.message.includes('Password should be at least')) {
          throw new Error('Le mot de passe doit contenir au moins 6 caractères.');
        }
        if (error.message.includes('weak and easy to guess') || error.message.includes('weak_password')) {
          throw new Error('Ce mot de passe est trop faible. Veuillez utiliser un mot de passe plus complexe (majuscules, minuscules, chiffres et caractères spéciaux).');
        }

        throw new Error(error.message);
      }
      
      console.log('[AUTH] Inscription réussie:', data.user?.email);
      return { data, error: null };
    } catch (error) {
      console.error('[AUTH] Erreur inscription inattendue:', error);
      throw error;
    }
  },

  /**
   * Déconnexion
   */
  signOut: async () => {
    if (!isSupabaseConfigured()) {
      // Allow logout even if not configured
      return { error: null };
    }
    
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('[AUTH] Erreur déconnexion:', error.message);
        throw error;
      }
      
      console.log('[AUTH] Déconnexion réussie');
      return { error: null };
    } catch (error) {
      console.error('[AUTH] Erreur déconnexion inattendue:', error);
      throw error;
    }
  },

  /**
   * Reset password
   */
  resetPassword: async (email: string) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase n\'est pas configuré. Veuillez cliquer sur "Connect to Supabase" en haut à droite pour configurer la connexion.');
    }
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
        redirectTo: `${window.location.origin}/auth/callback?type=recovery`
      });
      
      if (error) {
        console.error('[AUTH] Erreur reset password:', error.message);
        throw error;
      }
      
      console.log('[AUTH] Email de reset envoyé à:', email);
      return { error: null };
    } catch (error) {
      console.error('[AUTH] Erreur reset password inattendue:', error);
      throw error;
    }
  },

  /**
   * Récupérer l'utilisateur actuel
   */
  getUser: async () => {
    if (!isSupabaseConfigured()) {
      console.warn('[AUTH] Supabase non configuré - retour utilisateur null');
      return { user: null, error: null };
    }
    
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      
      if (error) {
        // Gestion spécifique des erreurs de connexion
        if (error.message.includes('Failed to fetch') || error.message.includes('fetch')) {
          console.warn('[AUTH] Erreur de connexion Supabase - mode hors ligne');
          return { user: null, error: null };
        }
        
        // Gestion des tokens expirés et sessions manquantes
        if (error.message.includes('JWT') || error.message.includes('token') || error.message.includes('Auth session missing')) {
          console.warn('[AUTH] Session manquante ou expirée, nettoyage automatique...');
          await supabase.auth.signOut();
          return { user: null, error: null };
        }
        
        console.error('[AUTH] Erreur récupération utilisateur:', error.message);
        throw error;
      }
      
      return { user, error: null };
    } catch (error) {
      console.error('[AUTH] Erreur getUser inattendue:', error);
      return { user: null, error };
    }
  },

  /**
   * Récupérer la session actuelle
   */
  getSession: async () => {
    if (!isSupabaseConfigured()) {
      console.warn('[AUTH] Supabase non configuré - retour session null');
      return { session: null, error: null };
    }
    
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        // Gestion spécifique des erreurs de connexion
        if (error.message.includes('Failed to fetch') || error.message.includes('fetch')) {
          console.warn('[AUTH] Erreur de connexion Supabase - mode hors ligne');
          return { session: null, error: null };
        }
        
        console.error('[AUTH] Erreur récupération session:', error.message);
        throw error;
      }
      
      return { session, error: null };
    } catch (error) {
      console.error('[AUTH] Erreur getSession inattendue:', error);
      return { session: null, error };
    }
  }
};

// Monitoring des événements d'authentification
supabase.auth.onAuthStateChange((event, session) => {
  console.log('[AUTH] État changé:', event, session?.user?.email || 'aucun utilisateur');
  
  if (event === 'SIGNED_OUT') {
    console.log('[AUTH] Utilisateur déconnecté');
  }
  
  if (event === 'TOKEN_REFRESHED') {
    console.log('[AUTH] Token rafraîchi avec succès');
  }
  
  if (event === 'SIGNED_IN') {
    console.log('[AUTH] Utilisateur connecté:', session?.user?.email);
  }
});

// Export par défaut
export default supabase;